import React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from '@aag-development/becreatives-ui';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { FieldComplex } from 'src/components/Form/hook-form/field-complex';
import { useToast } from 'src/components/ui/use-toast';
import { updateProjectFilesUrl } from 'src/lib/services/api/request-api/request-client';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';

interface ProjectFilesUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string) => void;
  requestId: string;
  initialValue?: string;
  title?: string;
  description?: string;
}

type FormValues = {
  project_files_url: string;
};

export const suiteUrlPrefix = 'suite://drive/f5329f71-f06d-43f1-ae53-5de6e93c3f2f/';
const validationSchema = yup.object().shape({
  project_files_url: yup
    .string()
    .required('This field is required')
    .test(
      'starts-with-suite',
      () => {
        return (
          <span>
            The URL is incorrect. For more information go to{' '}
            <a
              href="https://help.becreatives.co/how-to-copy-suite-folder-links"
              target="_blank"
              className="tw-text-blue-500"
            >
              https://help.becreatives.co/how-to-copy-suite-folder-links
            </a>
          </span>
        );
      },
      (value) => {
        return value ? value.startsWith(suiteUrlPrefix) : false;
      },
    )
    .test('is-complete-url', 'URL must contain content after the suite prefix', (value) => {
      return value ? value.length > suiteUrlPrefix.length : false;
    }),
});

export const ProjectFilesUrlModal: React.FC<ProjectFilesUrlModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  requestId,
  initialValue = '',
  title = 'Suite Request Folder Location',
  description = 'Please provide the URL to the project files',
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(validationSchema),
    defaultValues: {
      project_files_url: initialValue,
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ url }: { url: string }) => updateProjectFilesUrl(requestId, url),
    onSuccess: (_, { url }) => {
      toast({
        title: t('Success')!,
        description: t('Suite Request Folder Location updated successfully'),
        variant: 'success',
      });
      onSubmit(url);
      onClose();
    },
    onError: (error) => {
      toast({
        title: t('Error')!,
        description: t('Failed to update Suite Request Folder Location'),
        variant: 'destructive',
      });
    },
  });

  const handleFormSubmit = (data: FormValues) => {
    updateMutation.mutate({ url: data.project_files_url });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <FormProvider {...form}>
        <ModalBody>
          <p className="mb-4">{description}</p>
          <form id="project-files-url-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
            <FieldComplex
              name="project_files_url"
              label={t('Suite Request Folder Location (add link here):')}
              placeholder="https://..."
              error={form.formState.errors.project_files_url?.message}
              required
            >
              <Input
                id={'project_files_url'}
                {...form.register('project_files_url')}
                className={cn([form.formState.errors.project_files_url && 'tw-border-destructive'])}
              />
            </FieldComplex>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose} disabled={updateMutation.isLoading}>
            {t('Cancel')}
          </Button>
          <Button
            color="primary"
            type="submit"
            form="project-files-url-form"
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? t('Saving...') : t('Save')}
          </Button>
        </ModalFooter>
      </FormProvider>
    </Modal>
  );
};
